// PfmeaCellRenderer.tsx
// Extracted from PfmeaTablePage to reduce cyclomatic complexity
// Renders a single PFMEA table cell based on column key
import React from 'react';
import { PfmeaItem } from '../../types';

interface RenderCellProps {
    item: PfmeaItem;
    colKey: string;
}

const renderActionPriority = (value: string) => (
    <span className={`px-2 py-1 rounded-full text-xs font-bold ${value === 'H' ? 'ap-high' : value === 'M' ? 'ap-medium' : 'ap-low'
        }`}>
        {value}
    </span>
);

const renderRpn = (item: PfmeaItem) => {
    const rpn = item.severity * item.occurrence * item.detection;
    const color = rpn >= 100 ? 'text-red-500 font-bold' : rpn >= 50 ? 'text-amber-500' : 'text-green-500';
    return <span className={color}>{rpn}</span>;
};

const PROCESS_ELEMENT_COLORS: Record<string, string> = {
    Man: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    Machine: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
    Material: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    Method: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
};

const renderProcessElement = (value: string) => (
    <span className={`px-2 py-1 rounded text-xs ${PROCESS_ELEMENT_COLORS[value] || ''}`}>
        {value}
    </span>
);

const renderSpecialCharacteristic = (value: string) => {
    if (value === 'CC') return <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-500 text-white">CC</span>;
    if (value === 'SC') return <span className="px-2 py-0.5 rounded text-xs font-bold bg-amber-500 text-white">SC</span>;
    return <span className="text-[var(--text-secondary)]">—</span>;
};

const STATUS_COLORS: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    review: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    approved: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
};

const renderStatus = (value: string) => (
    <span className={`px-2 py-0.5 rounded text-xs capitalize ${STATUS_COLORS[value] || ''}`}>
        {value}
    </span>
);

const renderControlsList = (value: string[]) => {
    if (!Array.isArray(value) || value.length === 0) {
        return <span className="text-[var(--text-secondary)]">—</span>;
    }
    return (
        <div className="max-w-[200px]">
            <p className="text-sm truncate" title={value.join(', ')}>
                {value[0]}
                {value.length > 1 && <span className="text-[var(--text-secondary)]"> +{value.length - 1}</span>}
            </p>
        </div>
    );
};

export const PfmeaCellRenderer: React.FC<RenderCellProps> = ({ item, colKey }) => {
    const value = (item as unknown as Record<string, unknown>)[colKey];

    if (colKey === 'actionPriority') return renderActionPriority(String(value ?? ''));
    if (colKey === 'rpn') return renderRpn(item);
    if (colKey === 'processElement') return renderProcessElement(String(value ?? ''));
    if (colKey === 'specialCharacteristic') return renderSpecialCharacteristic(String(value ?? ''));
    if (colKey === 'status') return renderStatus(String(value ?? ''));
    if (colKey === 'preventionControls' || colKey === 'detectionControls') {
        return renderControlsList(value as string[]);
    }
    if (colKey === 'severity' || colKey === 'occurrence' || colKey === 'detection') {
        return <span className="font-mono font-bold">{String(value ?? '')}</span>;
    }

    return <>{value !== undefined && value !== null ? String(value) : '—'}</>;
};

export default PfmeaCellRenderer;
